// app/(tabs)/market/produits/[id].tsx
//
// Écran "Voir plus" : détail complet d'un produit, atteint depuis
// index.tsx (bouton Voir plus). Se contente de fetch le produit via
// useProduct(id) et de déléguer tout l'affichage au composant
// réutilisable components/market/DetailProduit.tsx — la même UI est
// aussi utilisée dans boutiques/[slug].tsx (via navigation, pas
// affichage inline).
//
// PATCH V16 : le TODO qui signalait `seller` comme absent de
// market_marketplace_feed est obsolète — la vue expose déjà owner_id,
// owner_nom, owner_prenom (confirmés dans la définition SQL de
// market_marketplace_feed), et DetailProduit.tsx les consomme
// directement (product.owner_id / product.owner_nom /
// product.owner_prenom dans handleContactSeller), sans passer par un
// objet `seller` séparé. Rien à ajouter côté backend ni ici — le
// bouton "Contacter le vendeur" fonctionne déjà avec les données
// actuelles de useProduct(id).
//
// PATCH i18n : tous les textes affichés à l'utilisateur passent par
// react-i18next (namespace market.produitDetail / market.common).

import React from 'react';
import { View, ActivityIndicator, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';

import { useProduct } from '@/hooks/market/useProduct';
import DetailProduit from '@/components/market/DetailProduit';

export default function ProductDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const { t } = useTranslation();

  const { product, loading, error, notFound, refresh } = useProduct(id);

  if (loading) {
    return (
      <View style={styles.centerContainer}>
        <ActivityIndicator size="small" color="#6610B1" />
        <Text style={styles.centerText}>{t('market.produitDetail.loading')}</Text>
      </View>
    );
  }

  if (notFound) {
    return (
      <View style={styles.centerContainer}>
        <Ionicons name="alert-circle-outline" size={48} color="#C4B5FD" />
        <Text style={styles.centerText}>{t('market.produitDetail.notFound')}</Text>
        <TouchableOpacity style={styles.backBtn} onPress={() => router.back()} activeOpacity={0.8}>
          <Text style={styles.backBtnText}>{t('market.common.back')}</Text>
        </TouchableOpacity>
      </View>
    );
  }

  if (error || !product) {
    return (
      <View style={styles.centerContainer}>
        <Ionicons name="cloud-offline-outline" size={48} color="#C4B5FD" />
        <Text style={styles.centerText}>
          {error === 'session_expired'
            ? t('market.common.sessionExpiredReconnect')
            : t('market.produitDetail.cannotLoad')}
        </Text>
        <TouchableOpacity style={styles.backBtn} onPress={refresh} activeOpacity={0.8}>
          <Text style={styles.backBtnText}>{t('market.common.retry')}</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return <DetailProduit product={product} />;
}

const styles = StyleSheet.create({
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
    gap: 12,
    backgroundColor: '#fff',
  },
  centerText: {
    fontSize: 14,
    color: '#999',
    textAlign: 'center',
  },
  backBtn: {
    marginTop: 8,
    paddingHorizontal: 20,
    paddingVertical: 10,
    backgroundColor: '#F3E8FF',
    borderRadius: 10,
  },
  backBtnText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#6610B1',
  },
});
